import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, ArrowUpRight, ChevronRight, Instagram } from "lucide-react";
import { SERVICES, CONTACT_INFO, LOGO_URL, NAV_ITEMS } from "@/data/siteData";

const Footer = () => {
  return (
    <footer data-testid="footer" className="relative bg-[#03060C] text-white border-t border-white/5 overflow-hidden">
      {/* Main Footer */}
      <div className="max-w-[1400px] mx-auto px-6 py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Company Info */}
          <div className="lg:col-span-4 pr-4">
            <img 
              src={LOGO_URL} 
              alt="Great International Glass" 
              className="h-14 mb-8 brightness-0 invert opacity-90" 
            />
            <p className="text-slate-400 leading-relaxed mb-8 max-w-sm text-[14px] font-light">
              Great International Glass & Aluminium Works LLC specializes in high-quality
              architectural glass and aluminium solutions for the UAE market.
            </p>
            <div className="flex gap-4">
              {[
                { icon: Instagram, href: CONTACT_INFO.instagram, label: "Instagram" },
                { icon: MessageCircle, href: `https://wa.me/${CONTACT_INFO.whatsapp.replace(/\+/g, '')}`, label: "WhatsApp" },
                { icon: Phone, href: `tel:${CONTACT_INFO.phones[0]}`, label: "Phone" },
                { icon: Mail, href: `mailto:${CONTACT_INFO.email}`, label: "Email" }
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target={social.label === "WhatsApp" ? "_blank" : undefined}
                  rel={social.label === "WhatsApp" ? "noopener noreferrer" : undefined}
                  className="w-10 h-10 rounded-full bg-white/[0.02] border border-white/10 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#D4AF37] hover:to-[#C9A032] hover:border-[#D4AF37] hover:shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:-translate-y-1 transition-all duration-300 group"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4 text-slate-300 group-hover:text-black transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-[14px] font-bold text-white mb-8 tracking-[0.2em] uppercase relative inline-block">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-1/3 h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent"></span>
            </h4>
            <ul>
              {NAV_ITEMS.map((item) => (
                <li key={item.label} className="mb-2 last:mb-0">
                  <Link
                    to={item.path}
                    className="group flex items-center text-slate-400 transition-all duration-300 text-[14px] font-light"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F3E5AB] opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    <span className="group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#D4AF37] group-hover:to-[#F3E5AB] transition-all duration-300">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-3">
            <h4 className="text-[14px] font-bold text-white mb-8 tracking-[0.2em] uppercase relative inline-block">
              Our Services
              <span className="absolute -bottom-2 left-0 w-1/3 h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent"></span>
            </h4>
            <ul>
              {SERVICES.map((service) => (
                <li key={service.id} className="mb-2 last:mb-0">
                  <Link
                    to={`/services/${service.slug}`}
                    className="group flex items-center text-slate-400 transition-all duration-300 text-[14px] font-light"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F3E5AB] opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    <span className="group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#D4AF37] group-hover:to-[#F3E5AB] transition-all duration-300">{service.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info & CTA */}
          <div className="lg:col-span-3">
            <h4 className="text-[14px] font-bold text-white mb-8 tracking-[0.2em] uppercase relative inline-block">
              Contact Us
              <span className="absolute -bottom-2 left-0 w-1/3 h-[2px] bg-gradient-to-r from-[#D4AF37] to-transparent"></span>
            </h4>
            <ul className="space-y-4">
              <li>
                <div className="flex items-start gap-4 group">
                  <div className="w-8 h-8 rounded-full bg-white/[0.02] border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:border-[#D4AF37]/50 group-hover:shadow-[0_0_10px_rgba(212,175,55,0.1)] transition-all duration-300">
                    <Phone className="w-3.5 h-3.5 text-[#D4AF37]" />
                  </div>
                  <div className="text-[14px] font-light pt-1.5">
                    {CONTACT_INFO.phones.map((phone, i) => (
                      <a key={i} href={`tel:${phone}`} className="block text-slate-400 hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-[#D4AF37] hover:to-[#F3E5AB] transition-all duration-300 mb-1 last:mb-0">
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>
              </li>
              <li>
                <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-4 text-[14px] font-light text-slate-400 group">
                  <div className="w-8 h-8 rounded-full bg-white/[0.02] border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:border-[#D4AF37]/50 group-hover:shadow-[0_0_10px_rgba(212,175,55,0.1)] transition-all duration-300">
                    <Mail className="w-3.5 h-3.5 text-[#D4AF37]" />
                  </div>
                  <span className="pt-1.5 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#D4AF37] group-hover:to-[#F3E5AB] transition-all duration-300">{CONTACT_INFO.email}</span>
                </a>
              </li>
              <li>
                <a href={CONTACT_INFO.mapLink} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 group">
                  <div className="w-8 h-8 rounded-full bg-white/[0.02] border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:border-[#D4AF37]/50 group-hover:shadow-[0_0_10px_rgba(212,175,55,0.1)] transition-all duration-300">
                    <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" />
                  </div>
                  <span className="text-[14px] font-light text-slate-400 leading-relaxed pt-1.5 group-hover:text-white transition-colors">{CONTACT_INFO.address}</span>
                </a>
              </li>
            </ul>

            {/* Premium Dark CTA Box */}
            <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/[0.05] hover:border-[#D4AF37]/30 transition-colors duration-500 relative overflow-hidden group shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4AF37]/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out pointer-events-none" />
              
              <p className="text-[13px] text-slate-400 mb-4 relative z-10 font-medium uppercase tracking-wider">Ready to start your project?</p>
              
              <Link
                to="/contact"
                className="inline-flex items-center justify-between w-full gap-3 bg-gradient-to-r from-[#D4AF37] to-[#C9A032] text-black font-bold text-xs uppercase tracking-[0.15em] py-3.5 px-5 rounded-xl hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all duration-300 relative z-10 group/btn"
              >
                Get a Quote <ArrowUpRight className="w-4 h-4 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5 bg-[#010305]">
        <div className="max-w-[1400px] mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-[13px] font-light">
              © {new Date().getFullYear()} Great International Glass & Aluminium Works LLC. All Rights Reserved.
            </p>
            <p className="text-slate-500 text-[13px] font-light">
              Designed and developed by{" "}
              <a
                href="https://anraone.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D4AF37] hover:text-white transition-colors"
              >
                anraone.com
              </a>
            </p>
            <div className="flex gap-6 text-[13px] text-slate-500 font-light">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
